import { type ReactNode, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api } from '../../shared/api/client';
import { Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { BibleTabs } from './Atlas';
import { bibleLink } from './BiblePage';

type Summary = { id: string; term: string };
const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const letterOf = (term: string) => norm(term).charAt(0).toUpperCase();
const CREDIT = "Diccionario bíblico: Easton's Bible Dictionary (M. G. Easton, 1897, dominio público). Traducción y adaptación al español: KEORA.";

const BOOKS = [
  'Génesis', 'Éxodo', 'Levítico', 'Números', 'Deuteronomio', 'Josué', 'Jueces', 'Rut', '1 Samuel', '2 Samuel', '1 Reyes', '2 Reyes', '1 Crónicas', '2 Crónicas', 'Esdras',
  'Nehemías', 'Ester', 'Job', 'Salmos', 'Proverbios', 'Eclesiastés', 'Cantares', 'Isaías', 'Jeremías', 'Lamentaciones', 'Ezequiel', 'Daniel', 'Oseas', 'Joel', 'Amós',
  'Abdías', 'Jonás', 'Miqueas', 'Nahúm', 'Habacuc', 'Sofonías', 'Hageo', 'Zacarías', 'Malaquías', 'Mateo', 'Marcos', 'Lucas', 'Juan', 'Hechos', 'Romanos',
  '1 Corintios', '2 Corintios', 'Gálatas', 'Efesios', 'Filipenses', 'Colosenses', '1 Tesalonicenses', '2 Tesalonicenses', '1 Timoteo', '2 Timoteo', 'Tito', 'Filemón',
  'Hebreos', 'Santiago', '1 Pedro', '2 Pedro', '1 Juan', '2 Juan', '3 Juan', 'Judas', 'Apocalipsis',
];
// Primero los nombres largos ("1 Juan" antes que "Juan") para que la expresión tome el libro completo.
const REF = new RegExp(`((?:${[...BOOKS].sort((a, b) => b.length - a.length).join('|')}) \\d{1,3}(?::\\d{1,3}(?:-\\d{1,3}(?::\\d{1,3})?)?)?)`, 'g');

/** Convierte las citas escritas en el texto ("Éxodo 6:20") en enlaces a la lectura. */
export function withRefLinks(text: string): ReactNode[] {
  return text.split(REF).map((part, i) =>
    i % 2 ? (
      <Link key={i} to={bibleLink(part)}>
        {part}
      </Link>
    ) : (
      part
    ),
  );
}

export function DictionaryPage() {
  const list = useAsync((signal) => api.get<{ items: Summary[]; total: number; attribution: string }>('/bible/dictionary', signal), []);
  const [q, setQ] = useState('');
  const [letter, setLetter] = useState('A');
  const items = list.status === 'ready' ? list.data.items : [];
  const key = norm(q.trim());
  const found = useMemo(() => (key ? items.filter((e) => norm(e.term).includes(key)).sort((a, b) => Number(!norm(b.term).startsWith(key)) - Number(!norm(a.term).startsWith(key))) : []), [key, items]);
  const letters = useMemo(() => [...new Set(items.map((e) => letterOf(e.term)))].filter((l) => /[A-Z]/.test(l)).sort(), [items]);
  const inText = useAsync(
    (signal) => (key.length >= 4 ? api.get<{ alsoIn: Summary[] }>(`/bible/dictionary?q=${encodeURIComponent(q.trim())}`, signal) : Promise.resolve({ alsoIn: [] as Summary[] })),
    [key],
  );
  const also = inText.status === 'ready' ? inText.data.alsoIn : [];
  return (
    <div className="page page--narrow bible-wide">
      <header className="page__header">
        <h1>Biblia</h1>
      </header>
      <BibleTabs active="diccionario" />
      <Card>
        <Field label="Buscar en el diccionario" name="palabra" value={q} onChange={(e) => setQ(e.target.value)} placeholder="gracia, efod, fariseos, maná…" autoComplete="off" />
      </Card>
      {list.status === 'loading' ? <Loading /> : null}
      {list.status === 'error' ? <ErrorState error={list.error} onRetry={list.reload} /> : null}
      {key ? (
        <>
          {found.length ? (
            <Card title={`Palabras (${found.length})`}>
              <ul className="list">
                {found.slice(0, 100).map((e) => (
                  <li key={e.id} className="list__item">
                    <Link to={`/app/biblia/diccionario/${e.id}`}>{e.term}</Link>
                  </li>
                ))}
              </ul>
            </Card>
          ) : also.length ? null : (
            <EmptyState title="Esa palabra todavía no está en el diccionario." />
          )}
          {also.length ? (
            <Card title="También se menciona en">
              <ul className="list topic-index">
                {also.map((e) => (
                  <li key={e.id}>
                    <Link to={`/app/biblia/diccionario/${e.id}`}>{e.term}</Link>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </>
      ) : list.status === 'ready' ? (
        <Card title={`${list.data.total} palabras`}>
          <div className="chips" role="group" aria-label="Letra">
            {letters.map((l) => (
              <button key={l} type="button" className={`chip${l === letter ? ' chip--active' : ''}`} aria-pressed={l === letter} onClick={() => setLetter(l)}>
                {l}
              </button>
            ))}
          </div>
          <ul className="list topic-index">
            {items
              .filter((e) => letterOf(e.term) === letter)
              .map((e) => (
                <li key={e.id}>
                  <Link to={`/app/biblia/diccionario/${e.id}`}>{e.term}</Link>
                </li>
              ))}
          </ul>
        </Card>
      ) : null}
      <p className="small muted atlas__credit">{CREDIT}</p>
    </div>
  );
}

export function DictionaryEntryPage() {
  const { entryId = '' } = useParams();
  const data = useAsync((signal) => api.get<{ id: string; term: string; text: string[]; attribution: string }>(`/bible/dictionary/${entryId}`, signal), [entryId]);
  if (data.status === 'loading') return <Loading />;
  if (data.status === 'error') return <ErrorState error={data.error} onRetry={data.reload} />;
  const e = data.data;
  return (
    <div className="page page--narrow bible-wide">
      <p className="small">
        <Link to="/app/biblia/diccionario">← Diccionario</Link>
      </p>
      <header className="page__header">
        <h1>{e.term}</h1>
      </header>
      <Card>
        <div className="dictionary-entry">
          {e.text.map((p, i) => (
            <p key={i}>{withRefLinks(p)}</p>
          ))}
        </div>
      </Card>
      <p className="small muted atlas__credit">{e.attribution}</p>
    </div>
  );
}

/** En la lectura: palabras del diccionario que citan este capítulo. */
export function ChapterDictionaryCard({ book, chapter, action }: { book: string; chapter: number; action?: ReactNode }) {
  const data = useAsync((signal) => api.get<{ items: Summary[] }>(`/bible/dictionary/chapter?book=${book}&chapter=${chapter}`, signal), [book, chapter]);
  if (data.status === 'loading') return <Card title="En el diccionario" action={action}><Loading /></Card>;
  if (data.status === 'error') return <Card title="En el diccionario" action={action}><ErrorState error={data.error} /></Card>;
  if (data.data.items.length === 0) return <Card title="En el diccionario" action={action}><p className="muted small">No hay palabras del diccionario para este capítulo.</p></Card>;
  return (
    <Card title="En el diccionario" action={action}>
      <p className="small">
        {data.data.items.map((e, i) => (
          <span key={e.id}>
            {i ? ', ' : ''}
            <Link to={`/app/biblia/diccionario/${e.id}`}>{e.term}</Link>
          </span>
        ))}
      </p>
    </Card>
  );
}
